import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  CrmLearningProfileAccessGate,
  useCrmLearningProfileRegistration,
} from '@features/crm-learning-profiles';
import { normalizeCrmCourseProfileNavigationReturnTo } from '@features/crm-course-profile';
import { completionFormQuery } from '@features/learner-document';
import { RegistrationCompletionFormPage } from '@pages/crm';
import { Alert, AlertDescription, Button, PageBackLink, Skeleton } from '@shared/ui';
import { firstAccessibleCrmCustomerRecordArea } from '@widgets/crm-customer-record-shell';

const searchSchema = z.object({
  returnTo: z
    .string()
    .max(4096)
    .optional()
    .transform((value) => normalizeCrmCourseProfileNavigationReturnTo(value)),
});

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/learning/registrations/$registrationId_/completion-form/$documentId'
)({
  component: RegistrationCompletionFormRoute,
  validateSearch: searchSchema,
});

function RegistrationCompletionFormRoute() {
  const { locale, userProfileId, registrationId, documentId } = Route.useParams();
  const { returnTo } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmLearningProfileAccessGate
        customerId={userProfileId}
        onAccessUnavailable={(allowedAreas) => {
          const fallback = firstAccessibleCrmCustomerRecordArea(allowedAreas);
          return (
            <Navigate
              params={{ userProfileId, locale }}
              replace
              to={fallback?.route ?? '/$locale/unauthorized'}
            />
          );
        }}
      >
        {(viewerAccessScope) => (
          <RegistrationCompletionForm
            viewerAccessScope={viewerAccessScope}
            locale={locale}
            userProfileId={userProfileId}
            registrationId={registrationId}
            documentId={documentId}
            onBack={() =>
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/learning/registrations/$registrationId',
                params: { locale, userProfileId, registrationId },
                search: { registrationProfileTab: 'completion-form', returnTo },
              })
            }
          />
        )}
      </CrmLearningProfileAccessGate>
    </main>
  );
}

function RegistrationCompletionForm({
  viewerAccessScope,
  locale,
  userProfileId,
  registrationId,
  documentId,
  onBack,
}: {
  viewerAccessScope: string;
  locale: string;
  userProfileId: string;
  registrationId: string;
  documentId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const registration = useCrmLearningProfileRegistration(
    { viewerAccessScope },
    registrationId,
    true
  );
  useQuery(
    completionFormQuery({
      profileRef: userProfileId,
      documentId,
      context: { kind: 'registration', id: registrationId },
      viewerAccessScope,
    })
  );
  if (registration.isLoading)
    return (
      <div className="space-y-4">
        <PageBackLink type="button" onClick={onBack}>
          {t('common.back', 'Back')}
        </PageBackLink>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  const detail = registration.data?.data;
  const completionForm: unknown = detail?.progress?.completionForm;
  const linkedDocumentId =
    typeof completionForm === 'object' && completionForm !== null && 'id' in completionForm
      ? completionForm.id
      : null;
  if (registration.isFetching && linkedDocumentId !== documentId)
    return (
      <div className="space-y-4">
        <PageBackLink type="button" onClick={onBack}>
          {t('common.back', 'Back')}
        </PageBackLink>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  if (
    registration.isError ||
    detail?.userProfileId !== userProfileId ||
    linkedDocumentId !== documentId
  ) {
    return (
      <div className="space-y-4">
        <PageBackLink type="button" onClick={onBack}>
          {t('common.back', 'Back')}
        </PageBackLink>
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {t('learnerDocument.loadErrorDescription', 'This document could not be loaded safely.')}
          </AlertDescription>
        </Alert>
        <Button type="button" variant="outline" onClick={() => void registration.refetch()}>
          {t('common.retry', 'Retry')}
        </Button>
      </div>
    );
  }
  return (
    <RegistrationCompletionFormPage
      key={`${viewerAccessScope}:${userProfileId}:${registrationId}:${documentId}`}
      locale={locale}
      profileRef={userProfileId}
      documentId={documentId}
      registrationId={registrationId}
      viewerAccessScope={viewerAccessScope}
      prefetched
      onBack={onBack}
    />
  );
}
